import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, HardHat, Truck, Package } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { eur, todayISO } from "@/lib/format";

const EMPTY = { cantiere_id: "", data: todayISO(), descrizione_lavori: "", operai: [], mezzi: [], subappaltatori: [] };

export default function RapportinoModal({ open, onOpenChange, cantieri, operai, mezzi, fornitori, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          cantiere_id: initial.cantiere_id || "",
          data: initial.data || todayISO(),
          descrizione_lavori: initial.descrizione_lavori || "",
          operai: (initial.operai || []).map((o) => ({ operaio_id: o.operaio_id, ore: String(o.ore ?? "") })),
          mezzi: (initial.mezzi || []).map((m) => ({ mezzo_id: m.mezzo_id, ore: String(m.ore ?? "") })),
          subappaltatori: (initial.subappaltatori || []).map((s) => ({ fornitore_id: s.fornitore_id || "", prezzo: String(s.prezzo ?? "") })),
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const mergeOpts = (opts, rows, idField) => {
    const ids = new Set(opts.map((o) => o.id));
    const seen = new Set();
    const extras = (rows || [])
      .filter((r) => r[idField] && !ids.has(r[idField]) && !seen.has(r[idField]) && seen.add(r[idField]))
      .map((r) => ({ id: r[idField], nome: `${r.nome || "—"} (non più attivo)` }));
    return [...opts, ...extras];
  };
  const operaiOpts = mergeOpts(operai, initial?.operai, "operaio_id");
  const mezziOpts = mergeOpts(mezzi, initial?.mezzi, "mezzo_id");
  const addRow = (key, row) => setForm((f) => ({ ...f, [key]: [...f[key], row] }));
  const removeRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));
  const setRow = (key, i, field, val) => setForm((f) => ({ ...f, [key]: f[key].map((r, idx) => idx === i ? { ...r, [field]: val } : r) }));

  const submit = async () => {
    if (!form.cantiere_id) return toast.error("Seleziona un cantiere.");
    if (!form.data) return toast.error("Seleziona la data.");
    const payload = {
      cantiere_id: form.cantiere_id,
      data: form.data,
      descrizione_lavori: form.descrizione_lavori?.trim() || "",
      operai: form.operai.filter((o) => o.operaio_id).map((o) => ({ operaio_id: o.operaio_id, ore: Number(o.ore) || 0 })),
      mezzi: form.mezzi.filter((m) => m.mezzo_id).map((m) => ({ mezzo_id: m.mezzo_id, ore: Number(m.ore) || 0 })),
      subappaltatori: form.subappaltatori.filter((s) => s.fornitore_id).map((s) => ({ fornitore_id: s.fornitore_id, prezzo: Number(s.prezzo) || 0 })),
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/rapportini/${initial.id}`, payload);
        toast.success("Rapportino aggiornato.");
      } else {
        await api.post("/rapportini", payload);
        toast.success("Rapportino salvato con successo.");
      }
      onOpenChange(false); onSaved?.();
    } catch (err) {
      toast.error(err.message || "Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  const RowList = ({ title, icon: Icon, listKey, options, optLabel, valueField, idField, unit }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5"><Icon className="h-4 w-4 text-amber-600" /> {title}</Label>
        <Button type="button" variant="outline" size="sm" className="h-7 gap-1" data-testid={`add-${listKey}-row`}
          onClick={() => addRow(listKey, listKey === "subappaltatori" ? { fornitore_id: "", prezzo: "" } : { [idField]: "", ore: "" })}>
          <Plus className="h-3.5 w-3.5" /> Aggiungi
        </Button>
      </div>
      {form[listKey].length === 0 && <p className="text-xs text-muted-foreground">Nessuna riga.</p>}
      {form[listKey].map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <Select value={row[idField]} onValueChange={(v) => setRow(listKey, i, idField, v)}>
            <SelectTrigger className="flex-1" data-testid={`${listKey}-select-${i}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent>
              {options.map((o) => <SelectItem key={o.id} value={o.id}>{o[optLabel]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" step="0.01" min="0" className="w-28" placeholder={unit}
            data-testid={`${listKey}-value-${i}`} value={row[valueField]}
            onChange={(e) => setRow(listKey, i, valueField, e.target.value)} />
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => removeRow(listKey, i)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="rapportino-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica Rapportino" : "Nuovo Rapportino"}</DialogTitle>
          <DialogDescription>Registra manodopera, mezzi e subappaltatori impiegati in cantiere.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Cantiere</Label>
              <Select value={form.cantiere_id} onValueChange={(v) => set("cantiere_id", v)}>
                <SelectTrigger data-testid="rapportino-cantiere-select"><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                <SelectContent>{cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" data-testid="rapportino-data-input" value={form.data} onChange={(e) => set("data", e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Descrizione dei lavori</Label>
            <Textarea rows={2} data-testid="rapportino-descrizione-input" placeholder="Es. Getto solaio primo piano, posa ponteggi..."
              value={form.descrizione_lavori} onChange={(e) => set("descrizione_lavori", e.target.value)} />
          </div>

          <RowList title="Operai (ore)" icon={HardHat} listKey="operai" options={operaiOpts} optLabel="nome" idField="operaio_id" valueField="ore" unit="Ore" />
          <RowList title="Mezzi (ore di utilizzo)" icon={Truck} listKey="mezzi" options={mezziOpts} optLabel="nome" idField="mezzo_id" valueField="ore" unit="Ore" />
          <RowList title="Subappaltatori (prezzo €)" icon={Package} listKey="subappaltatori" options={fornitori} optLabel="ragione_sociale" idField="fornitore_id" valueField="prezzo" unit="€" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="rapportino-save-button"
            className="bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva Rapportino"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
